import { supabase } from '../lib/supabase';

export type MatchStatus = 'upcoming' | 'live' | 'completed';

export interface MatchStatusCalculation {
  status: MatchStatus;
  startTime: Date;
  endTime: Date;
  isCustomEndTime: boolean;
}

export class MatchStatusService {
  /**
   * Calculate match status based on current time and match timing
   * @param startDate - Match start date/time
   * @param endDate - Match end date/time (optional, will be calculated if not provided)
   * @returns MatchStatusCalculation object
   */
  static calculateMatchStatus(startDate: string, endDate?: string): MatchStatusCalculation {
    const now = new Date();
    const startTime = new Date(startDate);
    
    // Calculate end time: start time + 3 hours (default) or use provided end time
    let endTime: Date;
    let isCustomEndTime = false;
    
    if (endDate) {
      endTime = new Date(endDate);
      isCustomEndTime = true;
    } else {
      endTime = new Date(startTime.getTime() + (3 * 60 * 60 * 1000)); // +3 hours
    }
    
    let status: MatchStatus;
    
    // Status logic:
    // Live: start time <= now < end time
    // Completed: now >= end time  
    // Upcoming: now < start time
    if (now >= endTime) {
      status = 'completed';
    } else if (now >= startTime && now < endTime) {
      status = 'live';
    } else {
      status = 'upcoming';
    }
    
    return {
      status,
      startTime,
      endTime,
      isCustomEndTime
    };
  }
  
  /**
   * Update match status in database for a specific match
   * @param matchId - Match ID to update
   * @param status - New status to set
   */
  static async updateMatchStatus(matchId: string, status: MatchStatus): Promise<void> {
    try {
      const { error } = await supabase
        .from('matches')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', matchId);
        
      if (error) {
        throw new Error(`Failed to update match status: ${error.message}`);
      }
      
      console.log(`✅ Match ${matchId} status updated to: ${status}`);
    } catch (error) {
      console.error('❌ Error updating match status:', error);
      throw error;
    }
  }
  
  /**
   * Update match status for all matches based on current time
   * This should be called periodically or when needed
   */
  static async updateAllMatchStatuses(): Promise<void> {
    try {
      console.log('🔄 Updating all match statuses...');
      
      // Get all active matches
      const { data: matches, error } = await supabase
        .from('matches')
        .select('id, start_date, end_date, status')
        .eq('active', true);
        
      if (error) {
        throw new Error(`Failed to fetch matches: ${error.message}`);
      }
      
      if (!matches || matches.length === 0) {
        console.log('ℹ️ No matches found to update');
        return;
      }
      
      let updatedCount = 0;
      
      for (const match of matches) {
        const calculation = this.calculateMatchStatus(match.start_date, match.end_date);
        
        // Only update if status has changed
        if (match.status !== calculation.status) {
          await this.updateMatchStatus(match.id, calculation.status);
          updatedCount++;
        }
      }
      
      console.log(`✅ Updated ${updatedCount} match statuses`);
    } catch (error) {
      console.error('❌ Error updating all match statuses:', error);
      throw error;
    }
  }
  
  /**
   * Get match status for a specific match
   * @param matchId - Match ID
   * @returns Promise<MatchStatusCalculation>
   */
  static async getMatchStatus(matchId: string): Promise<MatchStatusCalculation> {
    try {
      const { data: match, error } = await supabase
        .from('matches')
        .select('start_date, end_date')
        .eq('id', matchId)
        .single();
        
      if (error) {
        throw new Error(`Failed to fetch match: ${error.message}`);
      }
      
      if (!match) {
        throw new Error('Match not found');
      }
      
      return this.calculateMatchStatus(match.start_date, match.end_date);
    } catch (error) {
      console.error('❌ Error getting match status:', error);
      throw error;
    }
  }
  
  /**
   * Update match end time and recalculate status
   * @param matchId - Match ID
   * @param endDate - New end date/time
   */
  static async updateMatchEndTime(matchId: string, endDate: string): Promise<void> {
    try {
      // Get current match to get start_date
      const { data: match, error: fetchError } = await supabase
        .from('matches')
        .select('start_date')
        .eq('id', matchId)
        .single();
        
      if (fetchError) {
        throw new Error(`Failed to fetch match: ${fetchError.message}`);
      }
      
      const finalCalculation = this.calculateMatchStatus(match.start_date, endDate);
      
      // Update both end_date and status
      const { error } = await supabase
        .from('matches')
        .update({ 
          end_date: endDate,
          status: finalCalculation.status,
          updated_at: new Date().toISOString()
        })
        .eq('id', matchId);
        
      if (error) {
        throw new Error(`Failed to update match end time: ${error.message}`);
      }
      
      console.log(`✅ Match ${matchId} end time updated and status recalculated to: ${finalCalculation.status}`);
    } catch (error) {
      console.error('❌ Error updating match end time:', error);
      throw error;
    }
  }
}
